import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import Grid from '@mui/material/Grid2';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';

import dayjs, { ManipulateType, OpUnitType } from 'dayjs';
import { useSelector, useDispatch } from 'react-redux';

import useAxios from '../utils/useAxios';
import { setHomeReceipts } from '../actions';
import { StatCard, currency, number } from './ui';

const SUB_INTERVAL: Record<string, string> = { day: 'hour', week: 'day', month: 'day', year: 'month' };
const COMPARED_TO: Record<string, string> = { day: 'vs. ayer', week: 'vs. semana pasada', month: 'vs. mes pasado', year: 'vs. año pasado' };

const sum = (rows: any[], key: string) => rows.reduce((acc, row) => acc + (Number(row[key]) || 0), 0);

/** Fractional change; null when there is no previous value to compare against. */
const change = (current: number, past: number) => (past > 0 ? (current - past) / past : current > 0 ? null : 0);

const HomePrincipalCards = () => {
  const dispatch = useDispatch();
  const axios = useAxios();
  const navigate = useNavigate();
  const homeTimeInterval = useSelector((state: any) => state.homeTimeInterval) as string;

  const [loading, setLoading] = React.useState(true);
  const [stats, setStats] = React.useState({
    sales: [0, 0], earnings: [0, 0], clients: [0, 0], products: [0, 0],
  });

  React.useEffect(() => {
    const interval = homeTimeInterval as ManipulateType & OpUnitType;
    const startDate = dayjs().subtract(1, interval).startOf(interval);
    const endDate = dayjs().endOf(interval);
    const currentStart = endDate.startOf(interval);
    const range = { start_date: startDate.utc().format(), end_date: endDate.utc().format(), time_series: true };

    let cancelled = false;
    setLoading(true);
    (async () => {
      const [receipts, products] = await Promise.allSettled([
        axios.get('api/receipts/summary/', { params: { ...range, granularity: SUB_INTERVAL[homeTimeInterval] } }),
        axios.get('api/receipts/product-summary/', { params: { ...range, granularity: homeTimeInterval } }),
      ]);
      if (cancelled) return;

      const next = { sales: [0, 0], earnings: [0, 0], clients: [0, 0], products: [0, 0] };
      if (receipts.status === 'fulfilled') {
        const data = receipts.value.data as any[];
        dispatch(setHomeReceipts(data));
        const current = data.filter(r => !dayjs(r.date).isBefore(currentStart));
        const past = data.filter(r => dayjs(r.date).isBefore(currentStart));
        next.sales = [sum(current, 'total_count'), sum(past, 'total_count')];
        next.earnings = [sum(current, 'total_amount'), sum(past, 'total_amount')];
        next.clients = [sum(current, 'total_clients'), sum(past, 'total_clients')];
      }
      if (products.status === 'fulfilled') {
        const data = products.value.data as any[];
        next.products = [
          sum(data.filter(r => !dayjs(r.date).isBefore(currentStart)), 'total_count'),
          sum(data.filter(r => dayjs(r.date).isBefore(currentStart)), 'total_count'),
        ];
      }
      setStats(next);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [axios, dispatch, homeTimeInterval]);

  const compared = COMPARED_TO[homeTimeInterval];
  const cards = [
    { label: 'Ventas', value: number(stats.sales[0]), delta: change(stats.sales[0], stats.sales[1]), icon: <ReceiptLongOutlinedIcon />, to: '/admin/analysis' },
    { label: 'Ingresos', value: currency(stats.earnings[0]), delta: change(stats.earnings[0], stats.earnings[1]), icon: <PaymentsOutlinedIcon />, to: '/admin/analysis' },
    { label: 'Visitas de clientes', value: number(stats.clients[0]), delta: change(stats.clients[0], stats.clients[1]), icon: <PersonOutlinedIcon />, to: '/admin/clients' },
    { label: 'Productos vendidos', value: number(stats.products[0], 2), delta: change(stats.products[0], stats.products[1]), icon: <Inventory2OutlinedIcon />, to: '/admin/products' },
  ];

  return (
    <Grid container spacing={3}>
      {cards.map(card => (
        <Grid key={card.label} size={{ xs: 6, sm: 6, lg: 3 }}>
          <StatCard
            label={card.label}
            value={card.value}
            change={card.delta}
            changeLabel={compared}
            icon={card.icon}
            loading={loading}
            onClick={() => navigate(card.to)}
          />
        </Grid>
      ))}
    </Grid>
  );
};

export default HomePrincipalCards;
